import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Skull, Check, Sword } from "lucide-react";

/**
 * Bounty Contract of the Day — one Named Elite chosen deterministically
 * per UTC day. Players can spend gold to accept it; killing that elite
 * while the contract is active grants double loot and top billing on the
 * community Bounty Board.
 */
const RARITY_TINT = {
  rare: "#4A7BC7",
  epic: "#8A2BE2",
  legendary: "#E5C34A",
  mythic: "#E33333",
};

export default function BountyContract({ ch, setCh }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.bountyContractToday()
    .then(setState)
    .catch(() => setState(null));

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const buy = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.bountyContractBuy();
      setCh(res.character);
      await load();
      toast.success(`Contract signed: hunt ${res.bounty.name}!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Contract failed");
    } finally { setBusy(false); }
  };

  if (!state?.bounty) return null;
  const b = state.bounty;
  const tint = RARITY_TINT[b.guaranteed_drop_rarity] || "#4A7BC7";
  const owned = !!state.my_contract;
  const completed = state.my_contract?.completed;

  return (
    <div className="panel p-3" data-testid="bounty-contract">
      <div className="flex items-center gap-2 mb-2">
        <Sword className="w-3 h-3" strokeWidth={1.5} style={{ color: tint }} />
        <span className="font-heading text-[10px] tracking-widest uppercase" style={{ color: tint }}>
          Bounty Of The Day
        </span>
        <span className="ml-auto font-mono text-[9px] tracking-widest uppercase text-[#666]">
          {b.day}
        </span>
      </div>

      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 flex items-center justify-center border shrink-0"
          style={{ borderColor: tint, background: `${tint}18` }}
        >
          <Skull className="w-5 h-5" strokeWidth={1.5} style={{ color: tint }} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="font-heading text-sm tracking-widest uppercase truncate"
            style={{ color: tint, textShadow: `0 0 6px ${tint}55` }}
            data-testid="bounty-name"
          >
            {b.name}
          </div>
          <div className="font-body text-[11px] italic text-[#8c8c8c] mt-0.5 line-clamp-2">
            {b.flavor}
          </div>
          <div className="flex flex-wrap gap-2 mt-1 font-mono text-[9px] tracking-widest uppercase text-[#B0ADA5]">
            <span>Lv {b.min_level}+</span>
            <span style={{ color: tint }}>{b.guaranteed_drop_rarity} drop · 2x on completion</span>
          </div>
        </div>
      </div>

      {completed ? (
        <div
          data-testid="bounty-completed"
          className="mt-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase flex items-center justify-center gap-2"
        >
          <Check className="w-3 h-3" strokeWidth={1.5} /> Contract Complete
        </div>
      ) : owned ? (
        <div
          data-testid="bounty-owned"
          className="mt-3 py-2 border border-[#B89C3A] text-[#B89C3A] font-heading text-[10px] tracking-widest uppercase text-center"
        >
          Hunting · slay {b.name} for 2x loot
        </div>
      ) : (
        <button
          data-testid="bounty-buy"
          disabled={busy || ch.gold < b.contract_cost}
          onClick={buy}
          className="mt-3 w-full py-2 border font-heading text-[10px] tracking-widest uppercase flex items-center justify-center gap-2 transition-colors disabled:opacity-30"
          style={{
            borderColor: tint,
            color: tint,
            background: `${tint}10`,
          }}
        >
          <Coins className="w-3 h-3" strokeWidth={1.5} />
          Sign Contract · {b.contract_cost} Gold
        </button>
      )}
    </div>
  );
}
